import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormGroupDirective,
  NgForm,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/services/api/authentication/authentication.service';

// eslint-disable-next-line @angular-eslint/component-class-suffix
class ValueUsedMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, _form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && (control.dirty || control.touched));
  }
}

const requiredNameError = 'requiredName';

function requiredNameValidator(): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {
    if (!form.get('name').value) {
      const isNotPatient = (
        form.get('admin').value || form.get('ecoach').value || form.get('editor').value
      );
      return isNotPatient ? { [requiredNameError]: true } : null;
    }
    return null;
  };
}

type ResponseState = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

@Component({
  selector: 'app-dialog-admin-user-create',
  templateUrl: './dialog-admin-user-create.component.html',
  styleUrls: ['./dialog-admin-user-create.component.scss'],
})
export class DialogAdminUserCreateComponent implements OnInit, OnDestroy {
  readonly emailRegex =
    // eslint-disable-next-line max-len
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // Account form submission
  public accountForm: UntypedFormGroup;

  public createNewAccountResponse = new BehaviorSubject<ResponseState>('DEFAULT');

  public isNameAlreadyInUseSubject = new BehaviorSubject<boolean>(false);
  public isEmailAlreadyInUseSubject = new BehaviorSubject<boolean>(false);
  public invalidEmailSubject = new BehaviorSubject<boolean>(false);

  public matcher = new ValueUsedMatcher();

  private subscriptions: Subscription[] = [];

  constructor(
    private dialogRef: MatDialogRef<DialogAdminUserCreateComponent>,
    private authenticationService: AuthenticationService,
  ) {
    this.accountForm = new UntypedFormGroup(
      {
        email: new UntypedFormControl('', [Validators.required, Validators.pattern(this.emailRegex)]),
        name: new UntypedFormControl(''),
        firstname: new UntypedFormControl(''),
        lastname: new UntypedFormControl(''),
        admin: new UntypedFormControl(false),
        ecoach: new UntypedFormControl(false),
        editor: new UntypedFormControl(false),
      },
      {
        validators: [requiredNameValidator()],
      },
    );
    this.accountForm.markAllAsTouched();
  }

  public get f() {
    return this.accountForm;
  }

  ngOnInit(): void {}

  resetEmailError(): void {
    this.isEmailAlreadyInUseSubject.next(false);
    this.invalidEmailSubject.next(false);
  }

  resetNameError(): void {
    this.isNameAlreadyInUseSubject.next(false);
  }

  private resetError(): void {
    this.resetEmailError();
    this.resetNameError();
  }

  isInvalidForm() {
    return this.accountForm.invalid;
  }

  public registerNewAccount(): void {
    if (this.isInvalidForm()) {
      return;
    }
    this.resetError();
    const roleSlugs: string[] = [];
    if (this.f.get('admin').value) {
      roleSlugs.push('admin');
    }
    if (this.f.get('ecoach').value) {
      roleSlugs.push('ecoach');
    }
    if (this.f.get('editor').value) {
      roleSlugs.push('editor');
    }

    const email = this.f.get('email').value;
    const name = this.getTrimmedString(this.f.get('name').value);
    const firstname = this.getTrimmedString(this.f.get('firstname').value);
    const lastname = this.getTrimmedString(this.f.get('lastname').value);

    if (this.createNewAccountResponse.value === 'DEFAULT') {
      this.createNewAccountResponse.next('LOADING');
      this.subscriptions.push(
        this.authenticationService
          .createAccountAdmin(email, roleSlugs, name, firstname, lastname)
          .subscribe({
            next: () => {
              this.createNewAccountResponse.next('SUCCESS');
              setTimeout(() => {
                this.createNewAccountResponse.next('DEFAULT');
                this.dialogRef.close();
              }, 2500);
            },
            error: (value) => {
              const errors = value.error.errors[0]?.meta?.errors;
              if (errors) {
                const emailKey = 'data.attributes.email';
                if (emailKey in errors) {
                  const errorCode = errors[emailKey].at(0)?.error_code?.toString();
                  if (errorCode === "3007") {
                    this.isEmailAlreadyInUseSubject.next(true);
                  } else {
                    this.invalidEmailSubject.next(true);
                  }
                }
                const nameKey = 'data.attributes.name';
                if (nameKey in errors) {
                  this.isNameAlreadyInUseSubject.next(true);
                }
              }
              this.createNewAccountResponse.next('FAILURE');
              setTimeout(() => {
                this.createNewAccountResponse.next('DEFAULT');
              }, 2500);
            },
          }),
      );
    }
  }

  private getTrimmedString(s: string | null): string | undefined {
    return s || s === '' ? s.trim() : undefined;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
